/*
 * Copyright (C) 2018 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import {AlertManager} from '@instructure/platform-alerts'
import {ApolloProvider, createClient} from '@canvas/apollo-v3'
import {ErrorBoundary} from '@instructure/platform-error-boundary'
import {useScope as createI18nScope} from '@canvas/i18n'
import errorShipUrl from '@instructure/platform-images/assets/ErrorShip.svg'
import {GenericErrorPage} from '@instructure/platform-generic-error-page'
import {reportError, canvasErrorPageTranslations} from '@canvas/error-page-utils'
import ObserverOptions from '@canvas/observer-picker'
import {QueryClientProvider} from '@tanstack/react-query'
import {queryClient} from '@instructure/platform-query'
import {
  getHandleChangeObservedUser,
  autoFocusObserverPicker,
} from '@canvas/observer-picker/util/pageReloadHelper'
import React from 'react'
import {render} from '@canvas/react'
import StudentViewQuery from './components/StudentViewQuery'
import {View} from '@instructure/ui-view'

const client = createClient()
const I18n = createI18nScope('assignments_2')

export default function renderAssignmentsApp(env, elt) {
  render(
    <QueryClientProvider client={queryClient}>
      <ApolloProvider client={client}>
        <ErrorBoundary
          errorComponent={({error}) => (
            <GenericErrorPage
              imageUrl={errorShipUrl}
              onReportError={reportError}
              translations={canvasErrorPageTranslations}
              errorSubject={error.message}
              errorCategory="Assignments 2 Student Error Page"
              errorMessage={error.message}
              stack={error.stack}
            />
          )}
        >
          <AlertManager>
            <StudentViewQuery
              assignmentLid={ENV.ASSIGNMENT_ID.toString()}
              submissionID={ENV.SUBMISSION_ID?.toString()}
              reviewerSubmissionID={ENV.REVIEWER_SUBMISSION_ID?.toString()}
            />
          </AlertManager>
        </ErrorBoundary>
      </ApolloProvider>
    </QueryClientProvider>,
    elt,
  )

  const observerPickerContainer = document.getElementById('observer-picker-mountpoint')
  if (observerPickerContainer && ENV.OBSERVER_OPTIONS?.OBSERVED_USERS_LIST) {
    render(
      <View as="div" maxWidth="12em">
        <ObserverOptions
          autoFocus={autoFocusObserverPicker()}
          canAddObservee={!!ENV.OBSERVER_OPTIONS?.CAN_ADD_OBSERVEE}
          currentUserRoles={ENV.current_user_roles}
          currentUser={ENV.current_user}
          handleChangeObservedUser={getHandleChangeObservedUser()}
          observedUsersList={ENV.OBSERVER_OPTIONS.OBSERVED_USERS_LIST}
          renderLabel={I18n.t('Select a student to view. The page will refresh automatically.')}
        />
      </View>,
      observerPickerContainer,
    )
  }
}
